/**
 * ONE FRAME BUDGET FOR ALL MAIN-THREAD VOXEL WORK.
 *
 * Every voxel consumer that runs on the main thread — mesh applies, chunk ingest, the display commit,
 * skirts, batch compaction, worker dispatch — used to ration itself with a constant of its own (a count
 * here, a millisecond figure there, a vertex allowance on top). Each was tuned against a different
 * frame and none was revisited when the others changed, so the SUM was owned by nobody: ~10 ms of
 * nominal allowances before the walk, on a 16.7 ms frame.
 *
 * Now `VoxelWorld.update` computes one deadline per frame from the frame's measured headroom and hands
 * it to every consumer in priority order. A consumer's loop is `while (work && (n === 0 || now <
 * budgetEnd))` — the "at least one per frame" convention with the frame's clock instead of a private
 * one. Adding a consumer is one while-condition, never a new constant.
 *
 * SIZING. The frame cannot be shorter than the GPU takes, and GL is asynchronous: on a GPU-bound frame
 * the main thread idles at swap, so CPU work up to ~gpuMs is hidden behind the GPU. Hence the target is
 * `max(refreshPeriod, gpu)` — GPU time never subtracts from the allowance, it widens it. What DOES
 * subtract is the main-thread time the frame spends on everything else (physics, players, the CPU
 * render submit — which is also where geometry uploads land, so more geometry uploads this frame means a smaller
 * allowance next frame: negative feedback, no runaway). The inputs are rolling averages, so the
 * allowance is smooth rather than a per-frame control loop that oscillates.
 *
 * The floor keeps streaming alive when the CPU is over budget for reasons that are not voxel work: a
 * fixed 2 ms, or a fraction of the frame — on a 200 ms software-rasterizer frame 2 ms would be a 1 %
 * duty cycle and the headless probes would crawl. The ceiling bounds a pathological GPU stall (50 ms)
 * from becoming 45 ms of main-thread work that blocks input, even though it would not lengthen the
 * frame. Where the GPU timer is unavailable `gpuMs` is 0 and the target falls back to the refresh
 * period, never to zero.
 */

/** Compositor commit + rAF scheduling slack the frame needs after the update returns. */
export const BUDGET_GUARD_MS = 1.5;
/** Absolute floor, so every consumer's "at least one" still lands in a meaningful window. */
export const BUDGET_FLOOR_MS = 2;
/** Proportional floor: a slow frame keeps a proportional share for streaming. */
export const BUDGET_FLOOR_FRACTION = 0.15;
/** Ceiling: the main thread must stay responsive even when the GPU would hide more. */
export const BUDGET_CEIL_MS = 12;

/** 60 Hz — the target when the display's rate cannot be inferred from the frame intervals. */
export const DEFAULT_REFRESH_PERIOD_MS = 1000 / 60;

/** Refresh rates a display plausibly runs at, as periods, fastest first. */
const REFRESH_PERIODS_MS = [240, 144, 120, 90, 60].map((hz) => 1000 / hz);

/**
 * The display's refresh period inferred from the SHORTEST frame interval seen recently. A frame can
 * never be shorter than one vsync, so the minimum interval sits on the period (with jitter); the
 * closest plausible period at or above 90 % of it wins. Anything slower than 60 Hz (a slow display,
 * or a machine that never once made a full-rate frame) gets 60 Hz: the GPU term takes over there.
 */
export function refreshPeriodMs(minFrameIntervalMs: number): number {
  if (!(minFrameIntervalMs > 0)) return DEFAULT_REFRESH_PERIOD_MS;
  for (const p of REFRESH_PERIODS_MS) {
    if (minFrameIntervalMs <= p * 1.1) return p;
  }
  return DEFAULT_REFRESH_PERIOD_MS;
}

export interface FrameBudgetInputs {
  /** Refresh period of the display, ms — see refreshPeriodMs. */
  refreshPeriodMs: number;
  /** GPU time of the render, ms (rolling average; 0 where the timer is unavailable). */
  gpuMs: number;
  /** Main-thread time of the frame OUTSIDE voxel work, ms (rolling average). */
  fixedCpuMs: number;
  /** Observed frame interval, ms (rolling average) — the proportional floor's base. */
  frameIntervalMs: number;
}

/** Milliseconds of main-thread voxel work this frame may spend. Pure; see the module comment. */
export function voxelAllowanceMs(i: FrameBudgetInputs): number {
  const target = Math.max(i.refreshPeriodMs, i.gpuMs);
  const headroom = target - i.fixedCpuMs - BUDGET_GUARD_MS;
  const floor = Math.max(BUDGET_FLOOR_MS, BUDGET_FLOOR_FRACTION * i.frameIntervalMs);
  return Math.min(BUDGET_CEIL_MS, Math.max(floor, headroom));
}
